import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate, useParams} from 'react-router-dom';

import {useCountries} from 'src/hooks/dictionaries';
import {useNewsletterDictionary} from 'src/hooks/dictionaries/useNewsletterDictionary';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {FIELD, TOOL_NAME} from 'src/modules/create-user/constants';
import {validateColumnValue} from 'src/modules/create-user/user-bulk-import/utils';
import {getAccountGroups} from 'src/openapi/admin/apis/GetAccountGroupsApi';
import {getUsersByEmails} from 'src/openapi/admin/apis/GetUserByEmailApi';
import {UserStatus as UserStatuses} from 'src/openapi/admin/models/UserStatus';
import {UserType as UserTypes} from 'src/openapi/admin/models/UserType';
import {useAppDispatch} from 'src/store/hooks';

import Button from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';


import {
    selectBulkUserImportFile,
    selectBulkUserImportImportSettings,
    selectBulkUserImportSelectedStatus,
    selectBulkUserImportSelectedType,
    selectBulkUserImportTargetAccount,
    selectBulkUserImportTemplateNewsletter,
    setTemplateData,
    setValidateFileStep,
} from '../../services/user-bulk-import.slice';
import {
    INVALID_CITY_MESSAGE,
    INVALID_EMAIL_MESSAGE,
    INVALID_FIRST_NAME_MESSAGE,
    INVALID_JOB_TITLE_MESSAGE,
    INVALID_LAST_NAME_MESSAGE,
} from '../validate-file-step/constants';

import type {BulkImportUserType, UserBulkImportFileType} from 'src/modules/create-user/user-bulk-import/typedef';
import type {AccountGroup} from 'src/openapi/admin/models/AccountGroup';
import type {DictionaryItem} from 'src/openapi/admin/models/DictionaryItem';
import type {ExistedUserInfo} from 'src/openapi/admin/models/ExistedUserInfo';
import type {UserStatus} from 'src/openapi/admin/models/UserStatus';
import type {UserType} from 'src/openapi/admin/models/UserType';

export const UploadFileControls = () => {
    const queryParams = useParams<{accountId: string}>();
    const accountId = queryParams.accountId ? +queryParams.accountId : -1;

    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const {countries} = useCountries();
    const {newsletterDictionary} = useNewsletterDictionary();
    const {bulkSubmit} = useEventTracking();

    const location = useLocation();
    const prevPath = location.state?.prevPath;
    const navigate = useNavigate();
    const [isParsing, setIsParsing] = useState(false);
    const [accountGroups, setGroups] = useState<Array<AccountGroup> | null>(null);

    const targetAccount = useSelector(selectBulkUserImportTargetAccount);
    const templateNewsletter = useSelector(selectBulkUserImportTemplateNewsletter);
    const importSettings = useSelector(selectBulkUserImportImportSettings);
    const userStatus = useSelector(selectBulkUserImportSelectedStatus);
    const userType = useSelector(selectBulkUserImportSelectedType);
    const file = useSelector(selectBulkUserImportFile);

    useEffect(() => {
        if (!accountGroups && accountId) {
            apiDispatch(getAccountGroups({accountId})).then(response => {
                if (response.payload.success) {
                    setGroups(response.payload.data);
                }
            });
        }
    }, [accountGroups, accountId, apiDispatch]);

    const cancel = useCallback(() => {
        bulkSubmit({toolName: TOOL_NAME, submit: false});
        if (prevPath) {
            navigate(-1);
        } else {
            navigate('/');
        }
    }, [prevPath, bulkSubmit, navigate]);

    const getUsersFromSystem = async (data: UserBulkImportFileType[]) => {
        const emails = data.map(item => item.email?.toLowerCase());
        const map = new Map();
        if (emails && emails.length > 0) {
            const requestBody = emails.filter(email => email) as string[];
            const response = await apiDispatch(getUsersByEmails({requestBody}));
            if (response.payload.success) {
                response.payload.data.forEach(user => map.set(user.email, user));
            }
        }
        return map;
    };

    const getAccountGroupByName = (name?: string | number): AccountGroup | undefined => {
        if (!name || name === '') {
            return undefined;
        }
        if (typeof name === 'number') {
            return accountGroups?.find(group => group.id === +name);
        }
        return accountGroups?.find(group => group.name?.toLowerCase().trim() === name.toLowerCase().trim());
    };

    const normalizeNewsletterValue = (value: string): string => value.toLowerCase().trim().replace(/\s+/g, ' ');

    const getNewsletterTypes = (input: string): string[] => {
        if (input === '') {
            return [];
        }

        const inputValues = new Set(input
            .split(/[;\n]+/)
            .map(normalizeNewsletterValue)
            .filter(Boolean));
        const selectedNewsletterIds: string[] = [];

        newsletterDictionary.forEach(newsletterGroup => {
            const children = newsletterGroup.children ?? [newsletterGroup];
            const matchedChild = children.find(child =>
                inputValues.has(normalizeNewsletterValue(child.description)) ||
                inputValues.has(normalizeNewsletterValue(child.id)),
            );

            if (newsletterGroup.id === 'daily-pitch') {
                if (matchedChild) {
                    selectedNewsletterIds.push(matchedChild.id);
                }

                return;
            }

            children.forEach(child => {
                if (
                    inputValues.has(normalizeNewsletterValue(child.description)) ||
                    inputValues.has(normalizeNewsletterValue(child.id))
                ) {
                    selectedNewsletterIds.push(child.id);
                }
            });
        });

        return selectedNewsletterIds;
    };


    const getCountry = (searchTerm?: string): DictionaryItem | undefined => {
        if (!searchTerm || searchTerm === '') {
            return undefined;
        }
        return countries.find(country =>
            country.code.toLowerCase() === searchTerm.toLowerCase() ||
            country.description.toLowerCase() === searchTerm.toLowerCase(),
        );
    };

    const mapToBulkImportUserType = (
        data: UserBulkImportFileType[],
        usersFromSystem: Map<string, ExistedUserInfo>,
    ): BulkImportUserType[] => {
        const sanitizedData: UserBulkImportFileType[] = [];
        for (const item of data) {
            const email = item.email?.trim() || '';
            const sanitizedEmails = sanitizedData
                .map(sanitizedItem => sanitizedItem.email);

            if (email && !sanitizedEmails.includes(email)) {
                sanitizedData.push(item);
            }
        }

        return sanitizedData.map((item, index) => {
            const email = item[FIELD.EMAIL] ? item.email.toLowerCase() : '';
            const existUser = usersFromSystem.get(email);
            const id = existUser?.userId || index + 1;
            const firstName = item[FIELD.FIRST_NAME] || existUser?.firstName || '';
            const lastName = item[FIELD.LAST_NAME] || existUser?.lastName || '';
            const city = item[FIELD.CITY] || existUser?.city || '';
            const type = (userType === UserTypes.T_NA && existUser && existUser.typeCode !== UserTypes.T_NA)
                ? existUser.typeCode
                : userType;
            const status =
                (userStatus === UserStatuses.EMPTY && existUser && existUser.statusCode !== UserStatuses.EMPTY)
                    ? UserStatuses.DISABLED
                    : userStatus;
            const jobTitle = item[FIELD.JOB_TITLE] || existUser?.jobTitle || '';
            const notes = item[FIELD.NOTES] || '';
            const group = getAccountGroupByName(item[FIELD.GROUP])
                || getAccountGroupByName(existUser?.groupId) || null;
            const country = getCountry(item[FIELD.COUNTRY] || existUser?.countryCode || '') || null;
            const newsletters = item[FIELD.NEWSLETTERS]
                ? getNewsletterTypes(item[FIELD.NEWSLETTERS] as string)
                : existUser?.newsletterType || templateNewsletter || [];

            return ({
                id,
                targetAccount,
                email: {
                    value: email,
                    validationError: validateColumnValue('email', email)
                        ? INVALID_EMAIL_MESSAGE
                        : null,
                },
                firstName: {
                    value: firstName,
                    validationError: validateColumnValue('firstName', firstName)
                        ? INVALID_FIRST_NAME_MESSAGE
                        : null,
                },
                lastName: {
                    value: lastName,
                    validationError: validateColumnValue('lastName', lastName)
                        ? INVALID_LAST_NAME_MESSAGE
                        : null,
                },
                jobTitle: {
                    value: jobTitle,
                    validationError: validateColumnValue('jobTitle', jobTitle)
                        ? INVALID_JOB_TITLE_MESSAGE
                        : null,
                },
                city: {
                    value: city,
                    validationError: validateColumnValue('city', city)
                        ? INVALID_CITY_MESSAGE
                        : null,
                },
                userType: type as UserType,
                userStatus: status as UserStatus,
                notes: {
                    value: notes,
                    validationError: null,
                },
                importSettings,
                group,
                newsletters,
                country,
                priorAccount: existUser?.accountInfo || null,
            });
        });
    };

    const next = async () => {
        if (file) {
            setIsParsing(true);
            const usersFromSystem = await getUsersFromSystem(file);
            const data = mapToBulkImportUserType(file, usersFromSystem);
            dispatch(setTemplateData(data));
            setIsParsing(false);
        } else {
            dispatch(setTemplateData(null));
        }

        dispatch(setValidateFileStep());
    };

    return (
        <Group>
            <Button variant="Type.SECONDARY" onClick={cancel}>Cancel</Button>
            <Button
                variant="Type.PRIMARY"
                busy={isParsing}
                disabled={!file || !targetAccount || !templateNewsletter || !accountGroups}
                onClick={next}>
                Continue
            </Button>
        </Group>
    );
};
